/** Cosmetic tumbleweeds that roll across the arena, bouncing off the walls. */

import { ARENA_H, ARENA_W } from "@duelgame/shared";

const SIZE = 54;
const RADIUS = SIZE / 2;
/** Horizontal roll speed range (px/s). */
const MIN_SPEED = 60;
const MAX_SPEED = 130;
/** Cap on vertical bounce speed so it can't runaway over time. */
const MAX_VY = 80;

export interface TumbleweedState {
  x: number;
  y: number;
  vx: number;
  vy: number;
  angle: number;
}

function randSpeed(): number {
  return MIN_SPEED + Math.random() * (MAX_SPEED - MIN_SPEED);
}

function clampVy(vy: number): number {
  return Math.max(-MAX_VY, Math.min(MAX_VY, vy));
}

function freshWeed(): TumbleweedState {
  return {
    x: RADIUS + Math.random() * (ARENA_W - SIZE),
    y: RADIUS + Math.random() * (ARENA_H - SIZE),
    vx: randSpeed() * (Math.random() < 0.5 ? 1 : -1),
    vy: (Math.random() - 0.5) * 40,
    angle: 0,
  };
}

function freshWeeds(): TumbleweedState[] {
  return [freshWeed()];
}

function copyWeed(s: TumbleweedState): TumbleweedState {
  return { x: s.x, y: s.y, vx: s.vx, vy: clampVy(s.vy), angle: s.angle };
}

function stepWeed(w: TumbleweedState, step: number): void {
  w.x += w.vx * step;
  w.y += w.vy * step;

  if (w.x < RADIUS) {
    w.x = RADIUS;
    w.vx = randSpeed();
    w.vy = clampVy(w.vy + (Math.random() - 0.5) * 30);
  } else if (w.x > ARENA_W - RADIUS) {
    w.x = ARENA_W - RADIUS;
    w.vx = -randSpeed();
    w.vy = clampVy(w.vy + (Math.random() - 0.5) * 30);
  }

  if (w.y < RADIUS) {
    w.y = RADIUS;
    w.vy = Math.min(MAX_VY, 20 + Math.random() * 40);
  } else if (w.y > ARENA_H - RADIUS) {
    w.y = ARENA_H - RADIUS;
    w.vy = -Math.min(MAX_VY, 20 + Math.random() * 40);
  }

  // Keep horizontal in the intended band even after long sessions / desync
  const ax = Math.abs(w.vx);
  if (ax < MIN_SPEED || ax > MAX_SPEED) {
    w.vx = Math.sign(w.vx || 1) * randSpeed();
  }
  w.vy = clampVy(w.vy);

  w.angle += (w.vx / RADIUS) * step;
}

/** Host-authoritative tumbleweeds; guest predicts and snaps to sync. */
export class TumbleweedField {
  weeds: TumbleweedState[] = freshWeeds();

  reset(): void {
    this.weeds = freshWeeds();
  }

  update(dt: number): void {
    const step = Math.min(0.05, Math.max(0, dt));
    for (const w of this.weeds) stepWeed(w, step);
  }

  /** Hard snap from the host's snapshot. */
  applyRemote(list: readonly TumbleweedState[]): void {
    if (this.weeds.length !== list.length) {
      this.weeds = list.map(copyWeed);
      return;
    }
    for (let i = 0; i < list.length; i++) {
      const w = this.weeds[i]!;
      const s = list[i]!;
      w.x = s.x;
      w.y = s.y;
      w.vx = s.vx;
      w.vy = clampVy(s.vy);
      w.angle = s.angle;
    }
  }

  snapshot(): TumbleweedState[] {
    return this.weeds.map(copyWeed);
  }
}

export const TUMBLEWEED_SIZE = SIZE;
